import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    Flex,
    Grid,
    Heading,
    Input,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
    HStack,
    Badge,
    useToast,
    Spinner,
    Alert,
    AlertIcon,
    Switch,
    InputGroup,
    InputLeftElement,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Center
} from '@chakra-ui/react';
import {
    FiSearch,
    FiEdit3,
    FiArrowLeft,
    FiTarget,
    FiPhone,
    FiMail,
    FiUser
} from 'react-icons/fi';
import Pagination from 'react-js-pagination';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header';
import CustomBadge from '../../components/SharedComponent/Badge';
import moment from 'moment';
import 'moment-timezone';
import { getUserTimeZone } from '../WebHook-Logs';
import API from '../../helpers/axiosInstance';
import { getApiHeaders as getApiHeadersUtil, hasPermission } from '../../utils/DefaultFunction';
const CohortsDetails = ({ header }) => {
    const toast = useToast();
    const { id } = useParams();

    const [permissions, setPermissions] = useState([]);
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    const permParams = "marketing/cohorts";

    const fetchPermissions = async () => {
        setPermissionsLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`;
            const response = await API.get(url, { headers: getApiHeadersUtil() });
            setPermissions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching permissions:", error);
            setPermissions([]);
        } finally {
            setPermissionsLoading(false);
        }
    };
    useEffect(() => {
        fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const xPermission = hasPermission("cohort-view", permissions);
    const changeStatusPermission = hasPermission("cohort-change-status", permissions);
    const editPermission = hasPermission("cohort-edit", permissions);

    const [cohortData, setCohortData] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
    const [error, setError] = useState(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const navigate = useNavigate()
    // API Headers
    const getApiHeaders = () => ({
        "x-api-key": localStorage.getItem('api-key'),
        "x-access-token": localStorage.getItem('customer-portal'),
    });

    // Fetch cohort details (without customers)
    const fetchCohortDetails = async () => {
        if (!id) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const url = `${process.env.REACT_APP_COHORT}/cohorts/${id}/details`;
            const response = await API.get(url, { headers: getApiHeaders() });
            const data = response.data;

           

            if (data && data.data) {
                setCohortData(data.data);
             
            } else {
                setError(data.message || 'Failed to fetch segment details');
            }
        } catch (error) {
            console.error('Error fetching Segments details:', error);
            setError('Failed to fetch Segments details');
            toast({
                title: "Error",
                description: "Failed to fetch segment details",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch contacts/customers with pagination and search
    const fetchContacts = async (page = 1, search = '') => {
        if (!id) {
            return;
        }

        setIsLoadingCustomers(true);

        try {
            // Build query parameters
            const params = new URLSearchParams({
                page: page.toString(),
                ...(search && { search: search })
            });

            const url = `${process.env.REACT_APP_COHORT}/cohorts/${id}/contacts?${params}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            const data = response.data;

         

            if (data && data.status) {
                // Adjust these field names based on your actual API response structure
                setCustomers(data.data?.contacts || data.data?.customers || data.data || []);
                setPagination(data.pagination || data.data?.pagination || null);
              
            } else {
                setError(data.message || 'Failed to fetch contacts');
                toast({
                    title: "Error",
                    description: data.message || "Failed to fetch contacts",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error fetching contacts:', error);
            toast({
                title: "Error",
                description: "Failed to fetch contacts",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoadingCustomers(false);
        }
    };

    // Update cohort status
    const updateCohortStatus = async (newStatus) => {
        setUpdatingStatus(true);

        try {
            const url = `${process.env.REACT_APP_COHORT}/cohorts/${id}/status`;
            const response = await API.patch(url,
                { status: newStatus },
                { headers: { ...getApiHeaders(), 'Content-Type': 'application/json' } }
            );

            const data = response.data;

            if (data.status) {
                setCohortData(prev => ({
                    ...prev,
                    cohort: {
                        ...prev.cohort,
                        status: newStatus
                    },
                    overview: {
                        ...prev.overview,
                        status: newStatus
                    }
                }));

                toast({
                    title: "Success",
                    description: `Segment status updated to ${newStatus}`,
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: "Error",
                    description: data.message || "Failed to update segment status",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error updating segment status:', error);
            toast({
                title: "Error",
                description: "Failed to update segment status",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setUpdatingStatus(false);
        }
    };

    // Handle status toggle
    const handleStatusToggle = () => {
        const currentStatus = cohortData?.cohort?.status || cohortData?.overview?.status;
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        updateCohortStatus(newStatus);
    };

    // Handle search
    const handleSearch = (searchValue) => {
        setSearchTerm(searchValue);
        setCurrentPage(1);
        fetchContacts(1, searchValue);
    };

    // Handle pagination
    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchContacts(page, searchTerm);
    };

    // Handle actions
    const handleRefresh = () => {
        setCurrentPage(1);
        setSearchTerm('');
        // Refresh both cohort details and contacts
        fetchCohortDetails();
        fetchContacts(1, '');
    };

    const handleEdit = () => {

        navigate(`/cohort/edit/${id}`);
    };

    const handleBack = () => {

        navigate(-1);
    };

    // Load data on mount — wait for the permission check to finish and pass
    // before firing the real data fetch.
    useEffect(() => {
        if (permissionsLoading || !xPermission) return;
        // Load cohort details first
        fetchCohortDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, permissionsLoading, xPermission]);

    // Load contacts when cohort details are loaded
    useEffect(() => {
        if (cohortData) {
            fetchContacts(1, '');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cohortData]);

    if (permissionsLoading) {
        return (
            <>
                <Header header={header} />
                <Center h="400px"><Spinner size="xl" color="blue.500" thickness="4px" /></Center>
            </>
        );
    }
    if (!xPermission) {
        return (
            <>
                <Header header={header} />
                <Center h="400px">
                    <Box textAlign="center">
                        <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>No Access</Text>
                        <Text fontSize="sm" color="gray.500">You don't have permission to view this sections.</Text>
                    </Box>
                </Center>
            </>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <Box p={6} maxW="100%" mx="auto">
                <Box textAlign="center" py={20}>
                    <Spinner size="xl" color="blue.500" />
                    <Text mt={4} fontSize="lg" color="gray.600">Loading segment details...</Text>
                </Box>
            </Box>
        );
    }

    // Error state
    if (error || !cohortData) {
        return (
            <Box p={6} maxW="100%" mx="auto">
                <Alert status="error" mb={4}>
                    <AlertIcon />
                    <Box>
                        <Text fontWeight="bold">Error loading segment details</Text>
                        <Text fontSize="sm">{error || 'Segment not found'}</Text>
                    </Box>
                </Alert>
                <Box textAlign="center" py={10}>
                    <Button colorScheme="blue" mr={4} onClick={handleRefresh}>
                        Retry Loading
                    </Button>
                    <Button variant="outline" onClick={handleBack}>
                        Back to Segments
                    </Button>
                </Box>
            </Box>
        );
    }

    const currentStatus = cohortData?.cohort?.status || cohortData?.overview?.status;

    return (
        <>
            <Header header={`${cohortData.cohort?.name} - Segment Details`} />
            <Card className='card-style' style={{ margin: "20px 40px" }} shadow={'xl'}>
                <Box p={6} m={5} className='box-style-card'>
                    <Button

                        variant="outline"
                        onClick={handleBack}
                        aria-label="Back to segments"
                    > <FiArrowLeft /> Back to Segments</Button>
                    <Flex justify="space-between" align="center" mt={4}>
                        <HStack spacing={4}>

                            <VStack align="start" spacing={1}>
                                <Heading size="lg">{cohortData.cohort?.name}</Heading>
                                <Text color="gray.600">{cohortData.cohort?.description}</Text>
                            </VStack>
                        </HStack>

                        <HStack spacing={3}>
                            <HStack>
                                <Text fontSize="sm" fontWeight="medium">Status:</Text>
                                <Switch
                                    isChecked={currentStatus === 'active'}
                                    onChange={handleStatusToggle}
                                    isDisabled={updatingStatus || !changeStatusPermission}
                                    colorScheme="green"
                                    size="md"
                                />
                                <CustomBadge
                                    px={2}
                                    py={.8}
                                    borderRadius="full"
                                    fontSize="sm"
                                    fontWeight="medium"
                                    color={currentStatus === 'active' ? '#016630' : '#894b00'}
                                    bg={currentStatus === 'active' ? '#dcfce7' : '#fef9c2'}

                                >
                                    {currentStatus === 'active' ? 'Active' : 'Inactive'}
                                </CustomBadge>

                                {updatingStatus && <Spinner size="sm" />}
                            </HStack>
                            {/* 
                            <Button
                                leftIcon={<FiRefreshCw />}
                                variant="outline"
                                size="sm"
                                onClick={handleRefresh}
                            >
                                Refresh
                            </Button> */}

                            {editPermission && (
                                <Button
                                    leftIcon={<FiEdit3 />}
                                    colorScheme="blue"
                                    size="sm"
                                    onClick={handleEdit}
                                    className='updated-chakra-button'

                                >
                                    Edit
                                </Button>
                            )}
                        </HStack>
                    </Flex>


                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6} mb={8} mt={5}>
                        <Card>
                            <CardBody>
                                <Stat>
                                    <Flex align="center" mb={2} justifyContent={'space-between'}>
                                        <StatLabel>Total Customers
                                        </StatLabel>
                                        <Box color="blue.500" mr={2}>
                                            {/* <FiUsers /> */}
                                        </Box>

                                    </Flex>
                                    <StatNumber>
                                        {cohortData.cohort.total_contacts}
                                    </StatNumber>
                                    <StatHelpText>Customers in this segment

                                    </StatHelpText>
                                </Stat>
                            </CardBody>
                        </Card>

                        <Card>
                            <CardBody>
                                <Stat>
                                    <Flex align="center" mb={2} justifyContent={'space-between'}>
                                        <StatLabel>Created Date</StatLabel>
                                        <Box color="green.500" mr={2}>
                                            {/* <FiTarget /> */}
                                        </Box>

                                    </Flex>
                                    <StatNumber>

                                        {moment.utc(cohortData.overview?.created_date).tz(getUserTimeZone()).format('DD-MMM-YYYY ')}
                                    </StatNumber>
                                    <StatHelpText>Created By <strong>{cohortData?.overview?.created_by}</strong></StatHelpText>
                                </Stat>
                            </CardBody>
                        </Card>

                        <Card>
                            <CardBody>
                                <Stat>
                                    <Flex align="center" mb={2} justifyContent={'space-between'}>
                                        <StatLabel>Last Updated</StatLabel>

                                        <Box color="purple.500" mr={2}>
                                            {/* <FiTrendingUp /> */}
                                        </Box>
                                    </Flex>
                                    <StatNumber>
                                        {moment.utc(cohortData.overview?.last_updated).tz(getUserTimeZone()).format('DD-MMM-YYYY')}
                                    </StatNumber>
                                    <StatHelpText>
                                        Refresh Frequency : 
                                        <CustomBadge
                                            px={2}
                                            py={.8}
                                            borderRadius="full"
                                            fontSize="sm"
                                            fontWeight="medium"
                                            color={'#894b00'}
                                            bg={'#fef9c2'}

                                        >
                                            {cohortData?.cohort?.refresh_frequency}
                                        </CustomBadge>
                                    </StatHelpText>
                                </Stat>
                            </CardBody>
                        </Card>


                    </Grid>
                    {/* Overview Section */}
                    {/* <Card mb={6}>
                        <CardHeader>
                            <Heading size="md">Overview</Heading>
                            <Text color="gray.600" fontSize="sm">Basic information about this cohort</Text>
                        </CardHeader>
                        <CardBody>
                            <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                                <Flex direction={"column"} gap={4}>
                                    <VStack align="start" spacing={1}>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.600">Created By</Text>
                                        <Text fontWeight="semibold">
                                            {cohortData.overview?.created_by || cohortData.cohort?.created_by || 'analytics_team'}
                                        </Text>
                                    </VStack>

                                    <VStack align="start" spacing={1}>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.600">Created Date</Text>
                                        <Text fontWeight="semibold">
                                            {cohortData.overview?.created_date || cohortData.cohort?.created_at
                                                ? new Date(cohortData.overview?.created_date || cohortData.cohort?.created_at).toLocaleDateString('en-US', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })
                                                : 'N/A'
                                            }
                                        </Text>
                                    </VStack>

                                </Flex>

                                <Flex direction={"column"} gap={4}>
                                    <VStack align="start" spacing={1}>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.600">Last Updated</Text>
                                        <Text fontWeight="semibold">
                                            {cohortData.overview?.last_updated || cohortData.cohort?.updated_at
                                                ? new Date(cohortData.overview?.last_updated || cohortData.cohort?.updated_at).toLocaleDateString('en-US', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })
                                                : 'N/A'
                                            }
                                        </Text>
                                    </VStack>

                                    <VStack align="start" spacing={1}>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.600">Refresh Frequency</Text>
                                        <CustomBadge
                                            px={2}
                                            py={.8}
                                            borderRadius="full"
                                            fontSize="sm"
                                            fontWeight="medium"
                                            color={'#894b00'}
                                            bg={'#fef9c2'}

                                        >
                                            {cohortData?.cohort?.refresh_frequency}
                                        </CustomBadge>
                                    </VStack>
                                </Flex>
                                <Box bg="#f1f5f9" p={3} borderRadius={"md"}>
                                    <Flex align="center" justify="space-between" mb={2}>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.600">
                                            Status
                                        </Text>
                                        <CustomBadge
                                            px={2}
                                            py={.8}
                                            borderRadius="full"
                                            fontSize="sm"
                                            fontWeight="medium"
                                            color={currentStatus === 'active' ? '#016630' : '#894b00'}
                                            bg={currentStatus === 'active' ? '#dcfce7' : '#fef9c2'}

                                        >
                                            {currentStatus === 'active' ? 'Active' : 'Inactive'}
                                        </CustomBadge>
                                    </Flex>
                                    <Flex align="center" justify="space-between">
                                        <Text fontSize="sm" fontWeight="medium" color="gray.600">
                                            Customer Count
                                        </Text>
                                        <Text fontWeight="bold" fontSize="lg">
                                            {cohortData.cohort.total_contacts}
                                        </Text>
                                    </Flex>
                                </Box>


                            </Grid>
                        </CardBody>
                    </Card> */}

                    {/* Cohort Conditions */}
                    <Card mb={6} >
                        <CardHeader>
                            <Heading size="md">Segment Conditions</Heading>
                            <Text color="gray.600" fontSize="sm">The criteria that define which customers belong to this segment</Text>
                        </CardHeader>
                        <CardBody>
                            {cohortData.conditions?.conditions && cohortData.conditions.conditions.length > 0 ? (
                                <VStack spacing={4} align="stretch">
                                    {cohortData.conditions.conditions.map((condition, index) => (
                                        <Card key={condition.id || index} variant="outline">
                                            <CardBody>
                                                <HStack mb={2}>
                                                    <Box color="blue.500">
                                                        <FiTarget />
                                                    </Box>
                                                    <Text fontWeight="semibold">Event Condition {condition.id}</Text>
                                                </HStack>
                                                <Box bg="gray.50" p={3} borderRadius="md">
                                                    <Text fontSize="sm" fontFamily="mono">
                                                        {condition.display_text ||
                                                            `${condition.event} > ${condition.property} ${condition.operator} ${condition.value} (${condition.time_frame?.replace('_', ' ')})`}
                                                    </Text>
                                                </Box>
                                            </CardBody>
                                        </Card>
                                    ))}

                                    {/* Total Conditions Info */}
                                    {/* <Box mt={2}>
                                        <Text fontSize="sm" color="gray.600">
                                            Total Conditions: {cohortData.conditions.total_conditions}
                                        </Text>
                                    </Box> */}
                                </VStack>
                            ) : (
                                <Text color="gray.500">No conditions defined for this segment</Text>
                            )}
                        </CardBody>
                    </Card>

                    {/* Customers Section */}
                    <Card>
                        <CardHeader>
                            <Flex justify="space-between" align="center">
                                <VStack align="start" spacing={0}>
                                    <Heading size="md">
                                        Contacts ({pagination?.total_contacts})
                                    </Heading>
                                    <Text color="gray.600" fontSize="sm">Customers that match the segment conditions</Text>
                                </VStack>

                                <HStack>
                                    <InputGroup maxW="800px">
                                        <InputLeftElement pointerEvents="none">
                                            <FiSearch color="gray.400" />
                                        </InputLeftElement>
                                        <Input
                                            placeholder="Search contacts in this segment..."
                                            value={searchTerm}
                                            onChange={(e) => handleSearch(e.target.value)}
                                            width={"600px"}
                                        />
                                    </InputGroup>
                                    {/* <Button leftIcon={<FiFilter />} variant="outline" size="sm">
                                        Filter
                                    </Button> */}
                                </HStack>
                            </Flex>
                        </CardHeader>
                        <CardBody position="relative">
                            {isLoadingCustomers && (
                                <Box
                                    position="absolute"
                                    top="0"
                                    left="0"
                                    right="0"
                                    bottom="0"
                                    bg="rgba(255,255,255,0.8)"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    zIndex="1"
                                >
                                    <Spinner size="lg" color="blue.500" />
                                </Box>
                            )}

                            {customers.length > 0 ? (
                                <>
                                    <TableContainer>
                                        <Table variant="simple">
                                            <Thead>
                                                <Tr>
                                                    <Th>Customer Name</Th>
                                                    <Th>Contact Info</Th>
                                                    {/* <Th>Account Type</Th> */}
                                                    <Th>Total Messages</Th>
                                                    <Th>Last Activity</Th>
                                                    <Th>Status</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {customers.map((customer) => (
                                                    <Tr key={customer.customer_id || customer.id} _hover={{ bg: 'gray.50' }}>
                                                        <Td>
                                                            <VStack align="start" spacing={1}>
                                                                <HStack>
                                                                    <FiUser size={16} color="gray.500" />
                                                                    <Text fontWeight="semibold">{customer.name}</Text>
                                                                </HStack>
                                                                <Text fontSize="xs" color="gray.500">
                                                                    ID: {(customer.contact_id)?.toString().substring(0, 8)}...
                                                                </Text>
                                                            </VStack>
                                                        </Td>
                                                        <Td>
                                                            <VStack align="start" spacing={1}>
                                                                {customer.contact_info?.phone && (
                                                                    <HStack>
                                                                        <FiPhone size={12} />
                                                                        <Text fontSize="sm">{customer.contact_info.phone}</Text>
                                                                    </HStack>
                                                                )}
                                                                {customer.contact_info?.email && (
                                                                    <HStack>
                                                                        <FiMail size={12} />
                                                                        <Text fontSize="sm" color="blue.600">
                                                                            {customer.contact_info.email}
                                                                        </Text>
                                                                    </HStack>
                                                                )}
                                                            </VStack>
                                                        </Td>
                                                        {/* <Td>
                                                            <Badge
                                                                colorScheme={
                                                                    customer.account_type === 'Enterprise' ? 'purple' :
                                                                        customer.account_type === 'Premium' ? 'blue' : 'green'
                                                                }
                                                            >
                                                                {customer.account_type}
                                                            </Badge>
                                                        </Td> */}
                                                        <Td>
                                                            <Text fontWeight="semibold">
                                                                {customer.total_messages?.toLocaleString()}
                                                            </Text>
                                                        </Td>
                                                        <Td>
                                                            <Text fontSize="sm">{customer.last_activity}</Text>
                                                        </Td>
                                                        <Td>
                                                            <Badge
                                                                colorScheme={customer.status === 'Active' ? 'green' : 'gray'}
                                                            >
                                                                {customer.status}
                                                            </Badge>
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>

                                    <Box mt={6} display="flex" justifyContent="center">
                                        <Pagination
                                            activePage={currentPage}
                                            itemsCountPerPage={pagination.customers_per_page || pagination.per_page || 10}
                                            totalItemsCount={pagination.total_contacts}
                                            pageRangeDisplayed={5}
                                            onChange={handlePageChange}
                                            itemClass="page-item"
                                            linkClass="page-link"
                                            activeClass="active"
                                            activeLinkClass="active-link"

                                        />
                                    </Box>

                                    {/* Pagination Info */}
                                    {pagination && (
                                        <Box mt={4} textAlign="center">
                                            <Text fontSize="sm" color="gray.600">
                                                Showing {pagination.from || ((currentPage - 1) * (pagination.per_page || 10) + 1)} to {pagination.to || Math.min(currentPage * (pagination.per_page || 10), pagination.total_customers || pagination.total || customers.length)} of {pagination.total_customers || pagination.total_contacts || customers.length} contacts
                                                {searchTerm && ` (filtered by "${searchTerm}")`}
                                            </Text>
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <Box textAlign="center" py={10}>
                                    <Text color="gray.500">
                                        {isLoadingCustomers
                                            ? 'Loading contacts...'
                                            : searchTerm
                                                ? 'No contacts found matching your search criteria'
                                                : 'No contacts in this segment'
                                        }
                                    </Text>
                                    {searchTerm && !isLoadingCustomers && (
                                        <Button
                                            mt={3}
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleSearch('')}
                                        >
                                            Clear Search
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </CardBody>
                    </Card>


                </Box>
            </Card>
        </>
    );
};

export default CohortsDetails;